// PLACA ÎN EDITORUL SOLUȚIEI — partea fără desen a stratului de ochiuri din
// `EditorStructura`.
//
// Placa și ochiurile ei sunt ale FEM-ului: serverul le trimite la deschidere și
// la fiecare verificare (`editare.placile_editorului`), din aceeași rulare a
// Pasului 4 ca verdictele de amplasare. Omul o schimbă numai prin DIFERENȚE,
// pe nivelul grinzilor — `G.placi[nivel] = {adaugate, scoase}` (schema din
// `structure/geometrie/tipuri.py`): scoate un ochi, pune tavanul peste un
// balcon, desenează o consolă, trage de conturul uneia. Ochiurile nu se
// desenează de mână: le fac grinzile.
//
// PREVIZUALIZAREA. Între o schimbare și răspunsul serverului, în zonele
// schimbate placa se taie aici, cu traseele grinzilor (`previzualizarea`).
// Tăierea FEM-ului mai are o deschidere morfologică (`fem_shell._fara_fasii`)
// care rupe rama de placă de dincolo de grinda de fațadă; aici o imită
// prelungirea traseelor și filtrul fâșiilor. E o aproximare, deci stă numai în
// zonele schimbate și pleacă la răspunsul serverului.
//
// Coordonatele sunt ale geometriei: milimetri, în cadrul clădirii.

import type { MultiPolygon, Polygon } from "polygon-clipping";
import { taie } from "../viewer/taietor";
import { capatulGrinzii, type Geometrie, type Pt } from "./editorGeometrie";

export type PlaciNivel = NonNullable<Geometrie["placi"]>[string];
export type Cutie = [number, number, number, number];

/** O latură a ochiului, cu ce o reazemă (`fem_shell._laturi_ochi`). */
export type LaturaOchi = { a: Pt; b: Pt; reazem: "grinda" | "perete" | "contur_fara_reazem" };
export type OchiPlaca = {
  id: string;
  contur: Pt[];
  inele: Pt[][];
  arie_m2: number;
  lx_m: number;
  ly_m: number;
  deschidere_m: number;
  /** diametrul cercului înscris, între axele grinzilor */
  lumina_m: number;
  pe_grinzi_pct: number;
  pe_pereti_pct: number;
  pe_contur_pct: number;
  laturi: LaturaOchi[];
  /** consolele care cad pe ochi */
  console: string[];
  /** lumina trece de panoul maxim (`operatori.ochi_prea_mare`) */
  prea_mare?: boolean;
};
export type ConsolaPlaca = {
  id: string;
  tip: string;
  adanc_m: number | null;
  arie_m2: number | null;
  contur: Pt[];
  sursa: "arhitectura" | "om";
};
export type BalconFaraTavan = { id: string; contur: Pt[]; arie_m2: number; adanc_m: number | null };
/** Placa de la capul etajului `nivel`, cum o vede FEM-ul. */
export type PlacaNivel = {
  nivel: number;
  slab: Pt[][][];
  ochiuri: OchiPlaca[];
  console: ConsolaPlaca[];
  fara_tavan: BalconFaraTavan[];
};

/** Sub atât un inel nu e placă (`tipuri.PLACA_ARIE_MIN_MM2`): serverul l-ar
 *  refuza, deci diferențele mai mici nu se scriu. */
export const PLACA_ARIE_MIN_MM2 = 0.05e6;
/** Sub atât nu e ochi, e firimitură (`fem_shell.OCHI_MIN_M2`). */
const OCHI_MIN_MM2 = 0.8e6;
/** Grosimea lamei care taie placa pe traseul grinzii (`fem_shell.LAMA_OCHI_M`). */
const LAMA_MM = 10;
/** Cât trece lama de capetele traseului: rupe rama de placă de dincolo de
 *  grinda de fațadă, pe care FEM-ul o rupe prin deschiderea morfologică. */
const PRELUNGIRE_MM = 350;
/** O față mai îngustă de atât (aria pe semiperimetru) e o fâșie, nu un ochi
 *  (jumătate din `fem_shell.OCHI_LAT_MIN_M`, fiindcă A/(P/2) ≈ lățimea). */
const FASIE_MAX_MM = 250;
/** Cât se lărgește o zonă schimbată: ochiurile vecine ale unei grinzi trase
 *  sau șterse se refac și ele. */
const ZONA_MARGINE_MM = 500;

/** Pragurile SEMNALELOR din lista ochiurilor: ce merită privit, nu reguli de
 *  calcul (verificarea plăcii e a Pasului 16). */
export const SEMNAL_OCHI_MIC_M2 = 2.5;
export const SEMNAL_FARA_REAZEM_PCT = 25;

// --- geometrie de inele --------------------------------------------------------

const deschis = (r: Pt[]): Pt[] =>
  (r.length > 1 && r[0][0] === r[r.length - 1][0] && r[0][1] === r[r.length - 1][1]) ? r.slice(0, -1) : r;
const inchis = (r: Pt[]): Pt[] => { const o = deschis(r); return [...o, o[0]]; };
const poligon = (r: Pt[]): Polygon => [inchis(r) as [number, number][]];

export function ariaInel(r: Pt[]): number {
  let a = 0;
  for (let i = 0, n = r.length; i < n; i++) {
    const p = r[i], q = r[(i + 1) % n];
    a += p[0] * q[1] - q[0] * p[1];
  }
  return Math.abs(a) / 2;
}
const ariaPoli = (p: Pt[][]): number =>
  ariaInel(p[0]) - p.slice(1).reduce((s, r) => s + ariaInel(r), 0);
const ariaMulti = (m: MultiPolygon): number => m.reduce((s, p) => s + ariaPoli(p as Pt[][]), 0);
const perimetru = (r: Pt[]): number => {
  let s = 0;
  for (let i = 0; i < r.length; i++) {
    const a = r[i], b = r[(i + 1) % r.length];
    s += Math.hypot(b[0] - a[0], b[1] - a[1]);
  }
  return s;
};

export function cutie(pts: Pt[]): Cutie {
  let a = Infinity, b = Infinity, c = -Infinity, d = -Infinity;
  for (const [x, y] of pts) {
    if (x < a) a = x; if (y < b) b = y; if (x > c) c = x; if (y > d) d = y;
  }
  return [a, b, c, d];
}
const seAting = (a: Cutie, b: Cutie): boolean => a[0] <= b[2] && b[0] <= a[2] && a[1] <= b[3] && b[1] <= a[3];
const largita = (c: Cutie, m: number): Cutie => [c[0] - m, c[1] - m, c[2] + m, c[3] + m];

function inInel(p: Pt, r: Pt[]): boolean {
  let in_ = false;
  for (let i = 0, j = r.length - 1; i < r.length; j = i++) {
    const [xi, yi] = r[i], [xj, yj] = r[j];
    if ((yi > p[1]) !== (yj > p[1]) && p[0] < ((xj - xi) * (p[1] - yi)) / (yj - yi) + xi) in_ = !in_;
  }
  return in_;
}
/** Punctul e în poligon (inelul exterior, nu în găuri)? */
export const inPoligon = (p: Pt, poli: Pt[][]): boolean =>
  inInel(p, poli[0]) && !poli.slice(1).some((r) => inInel(p, r));

/** Unde se scrie eticheta unui poligon: mijlocul celei mai late tăieturi
 *  orizontale, încercată la câteva înălțimi. */
export function interior(poli: Pt[][]): Pt {
  const [x0, y0, x1, y1] = cutie(poli[0]);
  let best: Pt | null = null;
  let bw = -1;
  for (const t of [0.5, 0.35, 0.65, 0.2, 0.8, 0.42, 0.58]) {
    const y = y0 + (y1 - y0) * t;
    const xs: number[] = [];
    for (const r of poli)
      for (let i = 0; i < r.length; i++) {
        const a = r[i], b = r[(i + 1) % r.length];
        if ((a[1] > y) !== (b[1] > y)) xs.push(a[0] + ((y - a[1]) * (b[0] - a[0])) / (b[1] - a[1]));
      }
    xs.sort((a, b) => a - b);
    for (let i = 0; i + 1 < xs.length; i += 2) {
      const w = xs[i + 1] - xs[i];
      if (w > bw) { bw = w; best = [(xs[i] + xs[i + 1]) / 2, y]; }
    }
  }
  return best ?? [(x0 + x1) / 2, (y0 + y1) / 2];
}

/** Inelele exterioare ale unui multipoligon, fără cele fără arie de placă.
 *  Găurile se lasă: o diferență cu gol în ea e rară, iar schema poartă
 *  numai inele simple. */
function ineleExterioare(m: MultiPolygon): Pt[][] {
  return m.map((p) => deschis(p[0] as Pt[])).filter((r) => ariaInel(r) >= PLACA_ARIE_MIN_MM2)
    .map((r) => r.map(([x, y]) => [Math.round(x * 10) / 10, Math.round(y * 10) / 10] as Pt));
}
const multi = (inele: Pt[][]): MultiPolygon => inele.filter((r) => r.length >= 3).map(poligon);

/** Inelul cu colțurile arcelor desenate scoase (Douglas–Peucker, `tol_mm`):
 *  conturul unui balcon importat are la fiecare colț un arc de câțiva
 *  centimetri, pe care nu se trage colț cu colț. Colțurile rămase mai
 *  apropiate de `apropiate_mm` sunt un singur colț, rotunjit în desen: în
 *  locul lor intră colțul ascuțit, unde se întâlnesc laturile vecine. */
export function simplificaInel(r: Pt[], tol_mm = 30, apropiate_mm = 120): Pt[] {
  const s = douglasPeucker(r, tol_mm);
  const n = s.length;
  const departe = (a: Pt, b: Pt) => Math.hypot(a[0] - b[0], a[1] - b[1]) >= apropiate_mm;
  // ciorchinii de colțuri apropiate, pornind de la un colț departe de cel dinainte
  const start = s.findIndex((p, i) => departe(s[(i + n - 1) % n], p));
  if (start < 0) return s;
  const ciorchini: Pt[][] = [];
  for (let k = 0; k < n; k++) {
    const p = s[(start + k) % n];
    const ultim = ciorchini[ciorchini.length - 1];
    if (ultim && !departe(ultim[ultim.length - 1], p)) ultim.push(p);
    else ciorchini.push([p]);
  }
  if (ciorchini.length < 3) return s;
  const out: Pt[] = [];
  for (let i = 0; i < ciorchini.length; i++) {
    const c = ciorchini[i];
    if (c.length === 1) { out.push(c[0]); continue; }
    const prev = ciorchini[(i + ciorchini.length - 1) % ciorchini.length];
    const next = ciorchini[(i + 1) % ciorchini.length];
    const mij: Pt = [(c[0][0] + c[c.length - 1][0]) / 2, (c[0][1] + c[c.length - 1][1]) / 2];
    const x = intersectia(prev[prev.length - 1], c[0], c[c.length - 1], next[0]);
    // laturi aproape paralele: colțul ar zbura departe de arc
    out.push(x && Math.hypot(x[0] - mij[0], x[1] - mij[1]) < 3 * apropiate_mm ? x : mij);
  }
  return out;
}

/** Unde se întâlnesc dreptele (a, b) și (c, d); `null` dacă sunt paralele. */
function intersectia(a: Pt, b: Pt, c: Pt, d: Pt): Pt | null {
  const r = [b[0] - a[0], b[1] - a[1]], q = [d[0] - c[0], d[1] - c[1]];
  const den = r[0] * q[1] - r[1] * q[0];
  if (Math.abs(den) < 1e-9 * Math.hypot(...r) * Math.hypot(...q)) return null;
  const t = ((c[0] - a[0]) * q[1] - (c[1] - a[1]) * q[0]) / den;
  return [a[0] + t * r[0], a[1] + t * r[1]];
}

function douglasPeucker(r: Pt[], tol_mm: number): Pt[] {
  const pts = deschis(r);
  if (pts.length <= 4) return pts.map((p) => [p[0], p[1]]);
  const dist = (p: Pt, a: Pt, b: Pt) => {
    const dx = b[0] - a[0], dy = b[1] - a[1], L2 = dx * dx + dy * dy;
    let t = L2 ? ((p[0] - a[0]) * dx + (p[1] - a[1]) * dy) / L2 : 0;
    t = Math.max(0, Math.min(1, t));
    return Math.hypot(p[0] - a[0] - t * dx, p[1] - a[1] - t * dy);
  };
  const dp = (a: Pt[]): Pt[] => {
    if (a.length < 3) return a;
    let m = 0, k = 0;
    for (let i = 1; i < a.length - 1; i++) {
      const d = dist(a[i], a[0], a[a.length - 1]);
      if (d > m) { m = d; k = i; }
    }
    return m > tol_mm ? [...dp(a.slice(0, k + 1)).slice(0, -1), ...dp(a.slice(k))] : [a[0], a[a.length - 1]];
  };
  // inelul se taie în cele mai depărtate două colțuri, ca DP să aibă capete fixe
  let ia = 0;
  for (let i = 1; i < pts.length; i++) if (pts[i][0] + pts[i][1] < pts[ia][0] + pts[ia][1]) ia = i;
  let ib = 0, dm = -1;
  for (let i = 0; i < pts.length; i++) {
    const d = Math.hypot(pts[i][0] - pts[ia][0], pts[i][1] - pts[ia][1]);
    if (d > dm) { dm = d; ib = i; }
  }
  const rot = [...pts.slice(ia), ...pts.slice(0, ia)];
  const j = (ib - ia + pts.length) % pts.length;
  return [...dp(rot.slice(0, j + 1)).slice(0, -1), ...dp([...rot.slice(j), rot[0]]).slice(0, -1)];
}

// --- diferențele omului ----------------------------------------------------------

/** Plăcile omului pe nivel, cu ambele liste. */
export function placiNivel(G: Geometrie, nivel: number): PlaciNivel {
  const v = G.placi?.[String(nivel)];
  return { adaugate: v?.adaugate ?? [], scoase: v?.scoase ?? [] };
}

/** `G` cu plăcile nivelului înlocuite; un nivel fără nimic iese din listă. */
export function cuPlaciNivel(G: Geometrie, nivel: number, p: PlaciNivel): Geometrie {
  const placi = { ...(G.placi ?? {}) };
  if (p.adaugate.length || p.scoase.length) placi[String(nivel)] = p;
  else delete placi[String(nivel)];
  const out: Geometrie = { ...G, placi };
  if (!Object.keys(placi).length) delete out.placi;
  return out;
}

/** Adaugă `plus` la placă și scoate `minus` din ea: ce se adaugă iese din ce
 *  era scos și invers, ca cele două liste să nu se calce. */
export function schimbaPlaca(G: Geometrie, nivel: number, plus: Pt[][], minus: Pt[][]): Geometrie {
  const pc = taie();
  const v = placiNivel(G, nivel);
  let ad: MultiPolygon = multi(v.adaugate);
  let sc: MultiPolygon = multi(v.scoase);
  const P = multi(plus), M = multi(minus);
  if (P.length) { ad = pc.union(ad, ...P); sc = sc.length ? pc.difference(sc, ...P) : sc; }
  if (M.length) { sc = pc.union(sc, ...M); ad = ad.length ? pc.difference(ad, ...M) : ad; }
  return cuPlaciNivel(G, nivel, { adaugate: ineleExterioare(ad), scoase: ineleExterioare(sc) });
}

export const scoatePlaca = (G: Geometrie, nivel: number, inel: Pt[]): Geometrie =>
  schimbaPlaca(G, nivel, [], [inel]);
export const adaugaPlaca = (G: Geometrie, nivel: number, inel: Pt[]): Geometrie =>
  schimbaPlaca(G, nivel, [inel], []);

/** Placa pusă la loc în golul `inel`: golul iese din ce a scos omul. */
export function punePlacaLaLoc(G: Geometrie, nivel: number, inel: Pt[]): Geometrie {
  const pc = taie();
  const v = placiNivel(G, nivel);
  const sc = v.scoase.length ? pc.difference(multi(v.scoase), poligon(inel)) : [];
  return cuPlaciNivel(G, nivel, { adaugate: v.adaugate, scoase: ineleExterioare(sc) });
}

/** Ce aduce schimbarea conturului unei console din `vechi` în `nou`: ce a
 *  crescut (`plus`) și ce a scăzut (`minus`). Aceeași pereche se aplică și pe
 *  celelalte niveluri ale balconului repetat. */
export function diferentaContur(vechi: Pt[], nou: Pt[]): { plus: Pt[][]; minus: Pt[][] } {
  const pc = taie();
  return {
    plus: ineleExterioare(pc.difference(poligon(nou), poligon(vechi))),
    minus: ineleExterioare(pc.difference(poligon(vechi), poligon(nou))),
  };
}

/** Conturul unei console schimbat din `vechi` în `nou`: ce a crescut se
 *  adaugă, ce a scăzut se scoate. */
export function schimbaConturul(G: Geometrie, nivel: number, vechi: Pt[], nou: Pt[]): Geometrie {
  const { plus, minus } = diferentaContur(vechi, nou);
  return plus.length || minus.length ? schimbaPlaca(G, nivel, plus, minus) : G;
}

/** Cât se suprapun două inele: aria comună pe aria reuniunii. */
export function suprapunere(a: Pt[], b: Pt[]): number {
  const pc = taie();
  const i = ariaMulti(pc.intersection(poligon(a), poligon(b)));
  if (i <= 0) return 0;
  return i / ariaMulti(pc.union(poligon(a), poligon(b)));
}

/** Pe ce alte niveluri e «același» ochi sau «aceeași» consolă — cea mai
 *  suprapusă cu `inel`, peste `prag`: acolo merge o schimbare făcută pe
 *  «Toate nivelurile» (balconul și ochiul repetate pe etaje). */
export function peAlteNiveluri<T extends { contur: Pt[] }>(
  placi: PlacaNivel[], nivel: number, inel: Pt[], lista: (p: PlacaNivel) => T[], prag: number,
): { nivel: number; el: T }[] {
  const out: { nivel: number; el: T }[] = [];
  const c0 = cutie(inel);
  for (const p of placi) {
    if (p.nivel === nivel) continue;
    let best: T | null = null;
    let bv = prag;
    for (const el of lista(p)) {
      if (!seAting(cutie(el.contur), c0)) continue;
      const v = suprapunere(el.contur, inel);
      if (v >= bv) { bv = v; best = el; }
    }
    if (best) out.push({ nivel: p.nivel, el: best });
  }
  return out;
}

// --- previzualizarea ---------------------------------------------------------------

/** Traseul grinzii de la nod la nod prin capetele ei
 *  (`fem_shell._axa_pana_in_noduri`): la o lamelă lungă capătul stă departe de
 *  nod, iar bucata nod–capăt taie și ea placa. */
export function traseulGrinzii(G: Geometrie, gid: string): Pt[] {
  const g = G.grinzi.find((x) => x.id === gid);
  if (!g) return [];
  const ni = G.esv.find((e) => e.id === g.nod_i)?.nod;
  const nj = G.esv.find((e) => e.id === g.nod_j)?.nod;
  const a = capatulGrinzii(G, g, "i");
  const b = capatulGrinzii(G, g, "j");
  if (!ni || !nj || !a || !b) return [];
  const pts: Pt[] = [];
  for (const p of [ni, a, b, nj] as Pt[])
    if (!pts.length || Math.hypot(p[0] - pts[pts.length - 1][0], p[1] - pts[pts.length - 1][1]) > 1) pts.push(p);
  return pts;
}

/** Cutiile în care geometria nivelului s-a schimbat între `vechi` (cea
 *  verificată) și `nou`: grinzile mutate, puse sau șterse și plăcile omului
 *  schimbate, lărgite cu `ZONA_MARGINE_MM`. Goală = ochiurile serverului sunt
 *  încă ale geometriei de pe ecran. */
export function zoneleSchimbate(vechi: Geometrie, nou: Geometrie, nivel: number): Cutie[] {
  const out: Cutie[] = [];
  const cheie = String(nivel);
  const pe = (G: Geometrie) => new Map(G.grinzi.filter((g) => g.sectiune_per_nivel[cheie])
    .map((g) => [g.id, traseulGrinzii(G, g.id)] as const));
  const a = pe(vechi), b = pe(nou);
  for (const id of new Set([...a.keys(), ...b.keys()])) {
    const ta = a.get(id) ?? [], tb = b.get(id) ?? [];
    if (JSON.stringify(ta) === JSON.stringify(tb)) continue;
    const pts = [...ta, ...tb];
    if (pts.length) out.push(largita(cutie(pts), ZONA_MARGINE_MM));
  }
  const pv = placiNivel(vechi, nivel), pn = placiNivel(nou, nivel);
  const sv = new Set([...pv.adaugate, ...pv.scoase].map((r) => JSON.stringify(r)));
  const sn = new Set([...pn.adaugate, ...pn.scoase].map((r) => JSON.stringify(r)));
  for (const r of [...pv.adaugate, ...pv.scoase, ...pn.adaugate, ...pn.scoase]) {
    const k = JSON.stringify(r);
    if (sv.has(k) && sn.has(k)) continue;
    out.push(largita(cutie(r), ZONA_MARGINE_MM));
  }
  return out;
}

/** Un ochi de pe ecran: al serverului, sau unul previzualizat (fără id, fără
 *  laturi clasificate). */
export type OchiVazut = { id: string | null; poli: Pt[][]; arie_m2: number; server: OchiPlaca | null };

function lama(a: Pt, b: Pt): Polygon | null {
  const dx = b[0] - a[0], dy = b[1] - a[1], L = Math.hypot(dx, dy);
  if (L < 50) return null;
  const nx = (-dy / L) * LAMA_MM / 2, ny = (dx / L) * LAMA_MM / 2;
  return [[[a[0] + nx, a[1] + ny], [b[0] + nx, b[1] + ny], [b[0] - nx, b[1] - ny],
           [a[0] - nx, a[1] - ny], [a[0] + nx, a[1] + ny]]];
}
function prelungit(pts: Pt[]): Pt[] {
  if (pts.length < 2) return pts;
  const out = pts.map((p) => [p[0], p[1]] as Pt);
  const intinde = (p: Pt, q: Pt): Pt => {
    const L = Math.hypot(p[0] - q[0], p[1] - q[1]) || 1;
    return [p[0] + ((p[0] - q[0]) / L) * PRELUNGIRE_MM, p[1] + ((p[1] - q[1]) / L) * PRELUNGIRE_MM];
  };
  out[0] = intinde(out[0], out[1]);
  out[out.length - 1] = intinde(out[out.length - 1], out[out.length - 2]);
  return out;
}

/** Ochiurile de pe ecran pentru nivel: ale serverului (`placa`, verificate pe
 *  `Gv`) în afara zonelor schimbate, iar în ele placa refăcută din diferențele
 *  omului și tăiată cu traseele grinzilor din `G`. Fără zone, sunt exact ale
 *  serverului. */
export function ochiuriVazute(placa: PlacaNivel, G: Geometrie, Gv: Geometrie, nivel: number,
                              zone: Cutie[]): { ochiuri: OchiVazut[]; previzualizare: boolean } {
  const server = (o: OchiPlaca): OchiVazut =>
    ({ id: o.id, poli: [o.contur, ...o.inele], arie_m2: o.arie_m2, server: o });
  if (!zone.length) return { ochiuri: placa.ochiuri.map(server), previzualizare: false };
  const pc = taie();
  const pv = placiNivel(Gv, nivel), pn = placiNivel(G, nivel);
  let slab: MultiPolygon = placa.slab.map((p) => p.map(inchis) as Polygon);
  const fara = (a: Pt[][], b: Pt[][]): MultiPolygon =>
    !a.length ? [] : b.length ? pc.difference(multi(a), ...multi(b)) : multi(a);
  // ce a ieșit din adăugate pleacă, ce a ieșit din scoase se întoarce
  const plecate = fara(pv.adaugate, pn.adaugate);
  const intoarse = fara(pv.scoase, pn.scoase);
  if (plecate.length) slab = pc.difference(slab, ...plecate);
  if (pn.adaugate.length || intoarse.length) slab = pc.union(slab, ...multi(pn.adaugate), ...intoarse);
  if (pn.scoase.length) slab = pc.difference(slab, ...multi(pn.scoase));
  const lame: Polygon[] = [];
  for (const g of G.grinzi) {
    if (!g.sectiune_per_nivel[String(nivel)]) continue;
    const t = prelungit(traseulGrinzii(G, g.id));
    for (let i = 0; i + 1 < t.length; i++) {
      const l = lama(t[i], t[i + 1]);
      if (l) lame.push(l);
    }
  }
  const fete = lame.length ? pc.difference(slab, ...lame) : slab;
  const atinge = (c: Cutie) => zone.some((z) => seAting(z, c));
  const noi: OchiVazut[] = [];
  for (const f of fete) {
    const poli = (f as Pt[][]).map(deschis);
    const A = ariaPoli(poli);
    const per = poli.reduce((s, r) => s + perimetru(r), 0);
    if (A < OCHI_MIN_MM2 || A / (per / 2) < FASIE_MAX_MM) continue;
    if (atinge(cutie(poli[0]))) noi.push({ id: null, poli, arie_m2: A / 1e6, server: null });
  }
  const ramase = placa.ochiuri.filter((o) => !atinge(cutie(o.contur))
    && !noi.some((n) => inPoligon(interior([o.contur, ...o.inele]), n.poli)));
  return { ochiuri: [...ramase.map(server), ...noi], previzualizare: true };
}

// --- conturul unei console ---------------------------------------------------------

export const mutaColt = (r: Pt[], i: number, p: Pt): Pt[] => r.map((q, j) => (j === i ? p : q));
/** Un colț nou la mijlocul laturii `i` (de la colțul `i` la următorul). */
export function adaugaColt(r: Pt[], i: number): Pt[] {
  const a = r[i], b = r[(i + 1) % r.length];
  return [...r.slice(0, i + 1), [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2], ...r.slice(i + 1)];
}
/** Colțul `i` scos; `null` sub trei colțuri — atât are cea mai mică placă. */
export const scoateColt = (r: Pt[], i: number): Pt[] | null =>
  (r.length <= 3 ? null : r.filter((_, j) => j !== i));

/** Colțul tras se lipește de ce e aproape (`repere`: colțurile vecine, ale
 *  plăcii, nodurile): pe x și pe y separat, ca latura să iasă dreaptă; fără
 *  nimic aproape cade pe pasul de 5 cm. -> punctul și liniile de ghidaj. */
export function lipesteColt(p: Pt, repere: Pt[], tol_mm: number, pas_mm = 50):
    { p: Pt; ghidaje: { axa: 0 | 1; valoare: number }[] } {
  let x = Math.round(p[0] / pas_mm) * pas_mm;
  let y = Math.round(p[1] / pas_mm) * pas_mm;
  let bx = tol_mm, by = tol_mm;
  const ghidaje: { axa: 0 | 1; valoare: number }[] = [];
  let gx = false, gy = false;
  for (const q of repere) {
    const dx = Math.abs(q[0] - p[0]), dy = Math.abs(q[1] - p[1]);
    if (dx < bx) { bx = dx; x = q[0]; gx = true; }
    if (dy < by) { by = dy; y = q[1]; gy = true; }
  }
  if (gx) ghidaje.push({ axa: 0, valoare: x });
  if (gy) ghidaje.push({ axa: 1, valoare: y });
  return { p: [x, y], ghidaje };
}

/** Semnalele unui ochi al serverului, de la cel mai grav: ochiul prea mare,
 *  marginea fără reazem (fără consolă care s-o explice), firimitura. */
export function semnaleleOchiului(o: OchiPlaca): ("prea_mare" | "fara_reazem" | "mic")[] {
  const s: ("prea_mare" | "fara_reazem" | "mic")[] = [];
  if (o.prea_mare) s.push("prea_mare");
  if (o.pe_contur_pct >= SEMNAL_FARA_REAZEM_PCT && !o.console.length) s.push("fara_reazem");
  if (o.arie_m2 < SEMNAL_OCHI_MIC_M2) s.push("mic");
  return s;
}
